import { Actor, HttpAgent, type ActorSubclass, type Identity } from '@icp-sdk/core/agent';
import { Principal } from '@icp-sdk/core/principal';
import { idlFactory as factoryIdl, type _SERVICE as Factory, type Launch, type BaseToken, type Config, type UsdPrice, type Stats } from '../bindings/factory/declarations/factory.did';
import { idlFactory as marketIdl, type _SERVICE as Market, type MarketInfo, type Trade, type Candle } from '../bindings/market/declarations/market.did';
import { idlFactory as socialIdl, type _SERVICE as Social, type Trade as SocialTrade } from '../bindings/social/declarations/social.did';
import { poolPrice, poolSummaries, poolCandles, poolTrades, type PoolPrice, type PoolSummary } from './icpswap';

export type { Launch, BaseToken, Config, UsdPrice, Stats, MarketInfo, Trade, Candle, SocialTrade };
export const IDS = { factory: '3jz7z-jqaaa-aaaab-ag6kq-cai', social: 'phejn-2yaaa-aaaab-ahaaa-cai' };
export const HOST = 'https://icp-api.io';
export const NS = 1_000_000_000n;
export const nowNs = () => BigInt(Date.now()) * 1_000_000n;
export const principal = (value: string) => Principal.fromText(value.trim());

const timedFetch: typeof fetch = (input, init) => fetch(input, { ...init, signal: init?.signal ? AbortSignal.any([init.signal, AbortSignal.timeout(25_000)]) : AbortSignal.timeout(25_000) });
let agentPromise: Promise<HttpAgent> | undefined;
export function publicAgent(): Promise<HttpAgent> {
  return agentPromise ??= HttpAgent.create({ host: HOST, fetch: timedFetch }).catch(error => { agentPromise = undefined; throw error; });
}
export async function factory(): Promise<ActorSubclass<Factory>> {
  return Actor.createActor<Factory>(factoryIdl, { agent: await publicAgent(), canisterId: IDS.factory });
}
export async function market(id: string): Promise<ActorSubclass<Market>> {
  return Actor.createActor<Market>(marketIdl, { agent: await publicAgent(), canisterId: principal(id) });
}
export async function social(identity?: Identity): Promise<ActorSubclass<Social>> {
  const agent = identity ? await HttpAgent.create({ host: HOST, identity, fetch: timedFetch }) : await publicAgent();
  return Actor.createActor<Social>(socialIdl, { agent, canisterId: IDS.social });
}

export interface Overview { launches: Launch[]; config: Config; prices: UsdPrice[]; stats: Stats; fetchedAt: number; poolPrices: Map<string, PoolPrice>; pools: Map<string, PoolSummary>; dexError: string }
export async function loadOverview(): Promise<Overview> {
  const service = await factory();
  const [config, prices, stats, launches] = await Promise.all([service.config(), service.prices(), service.stats(), listAllMarkets(service)]);
  const poolPrices = new Map<string, PoolPrice>();
  const listed = launches.filter(row => row.graduated && row.icpswap_pool.length);
  const results = await Promise.allSettled(listed.map(async row => {
    const base = config.bases.find(item => item.token.ledger.toText() === row.base.toText())?.token;
    if (!base) throw Error('Unknown base ledger');
    poolPrices.set(row.market.toText(), await poolPrice(row.icpswap_pool[0]!.toText(), row.ledger.toText(), row.base.toText(), base.decimals));
  }));
  const analytics = listed.length ? await poolSummaries().then(pools => ({ pools, error: '' })).catch(() => ({ pools: new Map<string, PoolSummary>(), error: 'ICPSwap volume and liquidity are temporarily unavailable.' })) : { pools: new Map<string, PoolSummary>(), error: '' };
  return { launches, config, prices, stats, fetchedAt: Date.now(), poolPrices, pools: analytics.pools, dexError: [analytics.error, results.some(r => r.status === 'rejected') ? 'Some graduated pool prices could not be read.' : ''].filter(Boolean).join(' ') };
}

export async function listAllMarkets(service: { markets: (...args: Parameters<Factory['markets']>) => ReturnType<Factory['markets']> }): Promise<Launch[]> {
  // Read every page. The server can clamp the requested page size.
  const launches = new Map<string, Launch>();
  let offset = 0n;
  for (;;) {
    const page = await service.markets({ Newest: null }, offset, 100n, []);
    page.items.forEach(item => launches.set(item.market.toText(), item));
    offset += BigInt(page.items.length);
    if (offset >= page.total) break;
    if (!page.items.length) throw Error('Soon returned an incomplete token list. Please refresh.');
  }
  return [...launches.values()];
}

export async function loadDetail(id: string, hourly: boolean) {
  const [service, registry] = await Promise.all([market(id), factory()]);
  const [info, trades, candles, registered] = await Promise.all([service.info(), service.trades(500), service.candles(hourly, 0n), registry.launch_of(principal(id))]);
  if (!registered[0] || registered[0].market.toText() !== id) throw Error('This market is not registered on Soon.');
  if (info.id.toText() !== id || info.ledger.toText() !== registered[0].ledger.toText()) throw Error('The market identity or token ledger does not match Soon’s registry.');
  const pool = 'Graduated' in info.phase ? info.phase.Graduated.pool.toText() : null;
  const ledger = Actor.createActor<{ icrc1_total_supply: () => Promise<bigint> }>(({ IDL: T }) => T.Service({ icrc1_total_supply: T.Func([], [T.Nat], ['query']) }), { agent: await publicAgent(), canisterId: info.ledger });
  const [supplyResult, dex] = await Promise.all([
    ledger.icrc1_total_supply().then(value => ({ value, error: '' })).catch(() => ({ value: null, error: 'The token ledger supply could not be read.' })),
    pool ? Promise.allSettled([poolPrice(pool, info.ledger.toText(), info.base.ledger.toText(), info.base.decimals), poolCandles(pool, info.ledger.toText(), info.base.ledger.toText(), info.base.decimals, hourly), poolTrades(pool)]) : null,
  ]);
  return { info, trades, candles, mintedSupply: supplyResult.value, supplyError: supplyResult.error, pool, poolPrice: dex?.[0].status === 'fulfilled' ? dex[0].value : null, dexCandles: dex?.[1].status === 'fulfilled' ? dex[1].value : null, dexTrades: dex?.[2].status === 'fulfilled' ? dex[2].value : null, dexError: dex?.some(r => r.status === 'rejected') ? 'Some ICPSwap data is temporarily unavailable. Curve history remains available below.' : '', fetchedAt: Date.now() };
}

export async function walletTrades(owner: string, before?: bigint) {
  const service = await social();
  return service.trades_of(principal(owner), before === undefined ? [] : [before], 100);
}
export function baseFor(overview: Overview, ledger: string): BaseToken | undefined {
  return overview.config.bases.find(base => base.token.ledger.toText() === ledger)?.token;
}
export function usdFor(overview: Overview | null | undefined, ledger: string): number | null {
  const quote = overview?.prices.find(price => price.ledger.toText() === ledger);
  if (!quote || nowNs() - quote.at > 2n * 3600n * NS || quote.at > nowNs() + 60n * NS) return null;
  const value = quote.usd;
  return value !== undefined && value > 0 && Number.isFinite(value) ? value : null;
}
export function launchPrice(launch: Launch, base: BaseToken): number {
  return launch.price_y === 0n ? 0 : Number(launch.price_x) / Number(launch.price_y) * 10 ** (8 - base.decimals);
}
export function explain(error: unknown) {
  const message = error instanceof Error ? error.message : String(error);
  if (/fetch|network|timeout|time out|connect/i.test(message)) return 'Could not reach the Internet Computer. Check your connection and try again.';
  return message.length > 260 ? message.slice(0, 260) + '…' : message;
}
export function mustOk(value: { Ok: null } | { Err: string }) {
  if ('Err' in value) throw Error(value.Err);
}
